import { Trash2, User, Users } from "@tamagui/lucide-icons";
import { observer } from "mobx-react-lite";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Linking } from "react-native";
import Autolink from "react-native-autolink";
import { type SheetProps, XStack, YStack } from "tamagui";
import { replaceVariablesInText } from "../../shared/utils";
import { useStores } from "../core/stores";
import { Block } from "../ui/Block";
import { Body } from "../ui/Body";
import { Group } from "../ui/Group";
import type { InfoRowItemProps } from "../ui/InfoRow";
import { PopoverConfirm } from "../ui/PopoverConfirm";
import { Sheet } from "../ui/Sheet";
import { Subtext } from "../ui/Subtext";
import { Title } from "../ui/Title";
import { AvatarSize, UserAvatar } from "../ui/UserAvatar";
import type ActivityModel from "./ActivityModel";
import { ActivityEventType, REACTIONS, type Reaction } from "./types";

export const ReactionShareSheet: React.FC<
  SheetProps & { moduleUid: string; activityUid: string }
> = observer(({ moduleUid, activityUid, open, ...props }) => {
  const { usersStore, modulesStore, activitiesStore } = useStores();

  const module = modulesStore.byId.get(moduleUid);
  const activityFoundInStore = activitiesStore.byId.get(activityUid);

  let m: ActivityModel | undefined;
  if (activityFoundInStore) {
    m = activityFoundInStore;
  } else {
    m = module?.recentActivityModuleEvents.find((a) => a.uid === activityUid);
  }

  const toggleReaction = React.useCallback(
    (r: Reaction) => {
      if (m) {
        m.toggleReaction(r);
        props.onOpenChange?.(false);
      }
    },
    [m, props.onOpenChange],
  );

  if (!open || !m) {
    return null;
  }

  return (
    <Sheet sheetId="reactions" open={open} {...props}>
      <YStack gap="$space.large">
        <Title>Reactions</Title>

        <Group>
          {REACTIONS.map((r) => {
            const Icon = r.icon;

            if (!m) {
              return null;
            }

            const userListReacted = m.data.reactions
              .filter((reaction) => reaction.reaction === r.type)
              .map((rr) => usersStore.byId.get(rr.userUid)?.data.displayName);

            const infoRowItems: InfoRowItemProps[] = userListReacted.length
              ? [{ icon: Users, text: userListReacted.join(", ") }]
              : [];

            return (
              <Block
                key={r.type}
                grouped
                infoRowItems={infoRowItems}
                onPress={() => toggleReaction(r.type)}
              >
                <XStack alignItems="center" gap="$medium">
                  <Body>
                    <Icon />
                  </Body>
                  <Body>{r.title}</Body>
                </XStack>
              </Block>
            );
          })}
        </Group>
      </YStack>
    </Sheet>
  );
});

export const ActivityEventUI = observer(
  ({
    event,
    disableInteractions,
    prefixInfoRowItems,
  }: {
    event: ActivityModel;
    disableInteractions?: boolean;
    prefixInfoRowItems?: InfoRowItemProps[];
  }) => {
    const { t } = useTranslation();

    const [showReactionSheet, setShowReactionSheet] = React.useState(false);
    const isAuthor = event.user?.uid === event.plan.userInPlan?.uid;
    const isDeletable =
      isAuthor && event.data.type === ActivityEventType.Comment;

    const infoRowItems = useMemo<InfoRowItemProps[]>(() => {
      const items = prefixInfoRowItems ?? [];

      if (event.user) {
        items.push({
          text: event.user.data.displayName,
          icon: User,
        });
      }

      items.push(...event.reactionCounts);

      return items;
    }, [event, prefixInfoRowItems]);

    const textWithLinks = (
      <Autolink
        renderText={(text) => <Body>{text}</Body>}
        renderLink={(_text, match) => (
          <Body
            fontWeight={"bold"}
            textDecorationLine="underline"
            onPress={async (e) => {
              e.stopPropagation();
              e.preventDefault();

              const url = match.getMatchedText();

              if (await Linking.canOpenURL(url)) {
                Linking.openURL(url);
              }
            }}
          >
            {match.getAnchorHref()}
          </Body>
        )}
        text={
          event.data.text ??
          replaceVariablesInText(t(event.data.textSID), event.data.textData)
        }
      />
    );

    return (
      <>
        <Block
          onPress={() => {
            if (!disableInteractions) {
              setShowReactionSheet(true);
            }
          }}
          grouped
          infoRowItems={infoRowItems}
          icon={
            event.user ? (
              <UserAvatar size={AvatarSize.Smaller} userUid={event.user.uid} />
            ) : undefined
          }
        >
          {textWithLinks}
          {isDeletable && (
            <PopoverConfirm
              onConfirm={() => {
                event.deleteMe();
              }}
            >
              <XStack gap={"$space.small"}>
                <Trash2 color={"$muted"} size={"$small"} />
                <Subtext>Delete</Subtext>
              </XStack>
            </PopoverConfirm>
          )}
        </Block>

        <ReactionShareSheet
          moduleUid={event.data.moduleUid}
          activityUid={event.uid}
          open={showReactionSheet}
          onOpenChange={setShowReactionSheet}
        />
      </>
    );
  },
);
